// The history screen: what the archive knows about the person holding this device's token.
//
// Everything here is drawn from the server's answer. Nothing is counted, averaged or ranked in the
// browser, for the same reason the table is not: two clients doing their own arithmetic would
// sooner or later disagree, and the one with the database is the one that should be believed.

import { el, clear } from './dom.js';
import { Protocol, ServerProtocol } from '../generated/protocol.js';
import { pointsFormatterFor, pointsScaleFor } from '../game/table-state.js';

const dateFormat = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export class StatsView {
  #root;
  #content;
  #codeInput;
  #handlers;

  // handlers: { onBack, onAdoptToken }
  constructor(handlers) {
    this.#handlers = handlers;
    this.#content = el('div', { className: 'stats-content' });
    this.#codeInput = el('input', {
      id: 'recoveryCode',
      type: 'text',
      placeholder: 'incolla qui il codice',
    });
    this.#root = el('div', { className: 'screen' }, [
      el('h1', { className: 'title', text: 'Statistiche' }),
      this.#content,
      el('button', {
        className: 'button button-soft',
        type: 'button',
        text: 'Torna indietro',
        onclick: () => this.#handlers.onBack(),
      }),
    ]);
  }

  get element() {
    return this.#root;
  }

  showLoading() {
    clear(this.#content).append(el('p', { className: 'status', text: 'Lettura in corso...' }));
  }

  showError(message) {
    clear(this.#content).append(
      el('p', { className: 'status status-error', text: `Statistiche non disponibili: ${message}` }),
    );
  }

  // signedIn hides the device code below: it is the identity of whoever has no account, and
  // offering it to somebody who has one would be offering them a second, older self
  show(stats, myToken, { signedIn = false } = {}) {
    const played = stats[ServerProtocol.PlayedID] ?? 0;
    const won = stats[ServerProtocol.WonID] ?? 0;
    clear(this.#content).append(
      el('p', {
        className: 'status',
        text: played
          ? `${stats[Protocol.JSONPlayerNameID] ?? 'Tu'}: ${played} ${plural(played, 'partita', 'partite')}, ${won} ${plural(won, 'vinta', 'vinte')}`
          : 'Nessuna partita finita finora. Giocane una e torna qui.',
      }),
      this.#byGamePanel(stats[ServerProtocol.ByGameID] ?? []),
      this.#headToHeadPanel(stats[ServerProtocol.HeadToHeadID] ?? []),
      this.#recentPanel(stats[ServerProtocol.RecentGamesID] ?? []),
      signedIn ? null : this.#identityPanel(myToken),
    );
  }

  #byGamePanel(games) {
    if (!games.length) return null;
    const rows = games.map((game) =>
      row([
        game[ServerProtocol.DisplayNameID] ?? game[Protocol.JSONGameTypeID],
        String(game[ServerProtocol.PlayedID] ?? 0),
        String(game[ServerProtocol.WonID] ?? 0),
        formatAverage(game[ServerProtocol.AverageScoreID], game[Protocol.JSONGameTypeID]),
        formatAccuracy(game[ServerProtocol.BetsHitID], game[ServerProtocol.BetsMadeID]),
      ]),
    );
    return panel('Per gioco', table(['Gioco', 'Fatte', 'Vinte', 'Media', 'Dichiarazioni'], rows));
  }

  // Only people show up here: bots borrow real first names and change seat every game, so counting
  // them would turn this into a list of coincidences
  #headToHeadPanel(opponents) {
    if (!opponents.length) return null;
    const rows = opponents.map((opponent) => {
      const together = opponent[ServerProtocol.PlayedID] ?? 0;
      const ahead = opponent[ServerProtocol.AheadID] ?? 0;
      return row([
        opponent[Protocol.JSONPlayerNameID] ?? '?',
        String(together),
        `${ahead} su ${together}`,
      ]);
    });
    return panel('Testa a testa', table(['Amico', 'Insieme', 'Sei davanti'], rows));
  }

  #recentPanel(games) {
    if (!games.length) return null;
    const entries = games.map((game) => {
      // Every game keeps its own way of counting: Tressette in thirds, Hearts in penalties. The
      // archive stores the engine's raw number, so it is here that it goes back into the game's
      // own words instead of being printed as so many "pt"
      const formatPoints = pointsFormatterFor(game[Protocol.JSONGameTypeID]);
      return el('div', { className: 'stats-game' }, [
        el('div', { className: 'stats-game-head' }, [
          el('strong', {
            text: game[ServerProtocol.DisplayNameID] ?? game[Protocol.JSONGameTypeID],
          }),
          el('span', { text: formatDate(game[ServerProtocol.FinishedAtID]) }),
        ]),
        el(
          'ol',
          { className: 'stats-standings' },
          (game[ServerProtocol.PlayersID] ?? []).map((player) =>
            el('li', {}, [
              el('span', {
                className: player[ServerProtocol.IsBotID] ? 'stats-bot' : '',
                text: player[Protocol.JSONPlayerNameID] ?? '?',
              }),
              el('span', { text: formatPoints(player[ServerProtocol.ScoreID] ?? 0) }),
            ]),
          ),
        ),
      ]);
    });
    return panel('Ultime partite', entries);
  }

  // The token is the whole identity: there is no password to go with it, so copying it onto
  // another device is what "logging in there" means, and giving it away is giving away the history
  #identityPanel(myToken) {
    const display = el('input', { type: 'text', readonly: true, value: myToken ?? '' });
    return panel('Il tuo codice', [
      el('p', {
        className: 'stats-hint',
        text: 'Serve per ritrovare le tue partite su un altro dispositivo. Non darlo a nessuno: chi ce l\'ha risulta te.',
      }),
      display,
      el('button', {
        className: 'button button-soft',
        type: 'button',
        text: 'Copia',
        onclick: () => {
          display.select();
          navigator.clipboard?.writeText(display.value);
        },
      }),
      el('label', { for: 'recoveryCode', text: 'Oppure usa il codice di un altro dispositivo' }),
      this.#codeInput,
      el('button', {
        className: 'button button-soft',
        type: 'button',
        text: 'Usa questo codice',
        onclick: () => this.#handlers.onAdoptToken(this.#codeInput.value),
      }),
    ]);
  }
}

function panel(title, children) {
  return el('div', { className: 'panel' }, [el('h2', { text: title }), children].flat());
}

function table(headers, rows) {
  return el('div', { className: 'stats-table-scroll' }, [
    el('table', { className: 'stats-table' }, [
      el('thead', {}, [el('tr', {}, headers.map((header) => el('th', { text: header })))]),
      el('tbody', {}, rows),
    ]),
  ]);
}

function row(cells) {
  return el(
    'tr',
    {},
    cells.map((cell) => el('td', { text: cell })),
  );
}

function plural(count, one, many) {
  return count === 1 ? one : many;
}

// An average is not a whole number of anything, so it cannot be written the way a score is
// ("11 e 1/3"): it is turned into points with one decimal and given the game's own unit
function formatAverage(value, gameType) {
  if (typeof value !== 'number') return '—';
  const { perPoint, unit } = pointsScaleFor(gameType);
  return `${Math.round((value / perPoint) * 10) / 10} ${unit}`;
}

// Declaration games only. A game played without declarations has none, and an empty cell says so
// more honestly than a zero would
function formatAccuracy(hit, made) {
  if (!made) return '—';
  return `${hit ?? 0}/${made} (${Math.round(((hit ?? 0) / made) * 100)}%)`;
}

function formatDate(millis) {
  return millis ? dateFormat.format(new Date(millis)) : '';
}
